import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AuthGuard } from './components/AuthGuard';
import { Layout } from './components/Layout';
import { Dashboard } from './components/Dashboard';
import { DeviceList } from './components/DeviceList';
import { DeviceModal } from './components/DeviceModal';
import UserManagement from './components/UserManagement';
import LockRequestManagement from './components/LockRequestManagement';
import { Profile } from './components/Profile';
import { AuditLogs } from './components/AuditLogs';
import { DatabaseBackup } from './components/DatabaseBackup';
import { MobileDeviceControl } from './components/MobileDeviceControl';
import { BulkImportPage } from './components/BulkImportPage';
import { useDevices } from './hooks/useDevices';
import { useAdminStatus } from './hooks/useAdminStatus';
import { useFeatureFlag } from './hooks/useFeatureFlags';
import { Device } from './lib/supabase';
import { initializeFlagsmith } from './lib/flagsmith';

// Mobile detection utility
const isMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768);
};

// Mobile redirect component
const MobileRedirectWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isMobile() && (location.pathname === '/' || location.pathname === '/devices')) {
      navigate('/phone');
    }
  }, [navigate, location.pathname]);

  return <>{children}</>;
};

// Database backup wrapper with its own authentication
const DatabaseBackupWrapper = () => {
  const { isAuthenticated, isLoading, loginWithRedirect } = useAuth0();
  const { adminStatus } = useAdminStatus();
  const navigate = useNavigate();
  
  console.log('DatabaseBackupWrapper - Auth:', { isAuthenticated, isLoading, adminStatus }); // Debug log
  
  // Show loading while Auth0 is checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }
  
  // If not authenticated, show login
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Authentication Required</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Please sign in to access this page.
          </p>
          <button
            onClick={() =>
              loginWithRedirect({
                appState: {
                  returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
                },
              })
            }
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }
  
  // Show loading while admin status is being determined
  if (adminStatus === 'LOADING') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Verifying admin access...</p>
        </div>
      </div>
    );
  }
  
  // Check if user is approved admin
  if (adminStatus === 'APPROVED') {
    return <DatabaseBackup />;
  }
  
  // Access denied for non-admin users
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Access Denied</h1>
        <p className="text-gray-600 dark:text-gray-400 mb-6">
          This page requires administrator privileges.
        </p>
        <p className="text-xs text-gray-500 mb-4">Current status: {adminStatus}</p>
        <button
          onClick={() => navigate('/')}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          Return to Dashboard
        </button>
      </div>
    </div>
  );
};

interface MainAppLayoutProps {
  showProfile: boolean;
  activeTab: string;
  displayedTab: string;
  isTransitioning: boolean;
  adminStatus: 'LOADING' | 'NOT_FOUND' | 'PENDING' | 'APPROVED' | 'DENIED';
  showLockRequests: boolean;
  deviceFilter: 'all' | 'locked' | 'unlocked';
  selectedDevice: Device | null;
  isDeviceModalOpen: boolean;
  onTabChange: (tab: string) => void;
  onNavigateToProfile: () => void;
  onBackFromProfile: () => void;
  onCloseDeviceModal: () => void;
  onSelectDevice: (device: Device | null) => void;
  renderContent: (tab: string) => React.ReactNode;
}

const MainAppLayout: React.FC<MainAppLayoutProps> = ({
  showProfile,
  activeTab,
  displayedTab,
  isTransitioning,
  adminStatus,
  showLockRequests,
  deviceFilter,
  selectedDevice,
  isDeviceModalOpen,
  onTabChange,
  onNavigateToProfile,
  onBackFromProfile,
  onCloseDeviceModal,
  onSelectDevice,
  renderContent,
}) => {
  const {
    devices,
    loading,
    error,
    addDevice,
    updateDevice,
    deleteDevice,
    bulkImportDevices,
    clearError,
  } = useDevices();

  // Auto-clear transient device errors after display.
  useEffect(() => {
    if (error) {
      const timer = setTimeout(clearError, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  const handleSaveDevice = async (deviceData: Omit<Device, 'id' | 'created_at' | 'updated_at'>) => {
    if (!selectedDevice) {
      return { success: false, error: 'No device selected' };
    }

    const result = await updateDevice(selectedDevice.id, deviceData);
    if (result.success) {
      onCloseDeviceModal();
      onSelectDevice(null);
    }

    return result;
  };

  if (showProfile) {
    return <Profile onBack={onBackFromProfile} />;
  }

  return (
    <Layout
      activeTab={activeTab}
      onTabChange={onTabChange}
      adminStatus={adminStatus}
      onNavigateToProfile={onNavigateToProfile}
      showLockRequests={showLockRequests}
    >
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <p className="text-red-700 dark:text-red-400">{error}</p>
            <button
              onClick={clearError}
              className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm font-medium"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
      <div
        className={`transition-opacity duration-200 ${
          isTransitioning ? 'opacity-0' : 'opacity-100'
        }`}
      >
        {displayedTab === 'devices' ? (
          <>
            <DeviceList
              devices={devices}
              loading={loading}
              onUpdateDevice={updateDevice}
              onDeleteDevice={deleteDevice}
              onAddDevice={addDevice}
              onBulkImport={bulkImportDevices}
              initialFilter={deviceFilter}
            />
            <DeviceModal
              isOpen={isDeviceModalOpen}
              onClose={() => {
                onCloseDeviceModal();
                onSelectDevice(null);
              }}
              device={selectedDevice}
              mode="view"
              onSave={handleSaveDevice}
            />
          </>
        ) : (
          renderContent(displayedTab)
        )}
      </div>
    </Layout>
  );
};

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [displayedTab, setDisplayedTab] = useState('dashboard');
  const [deviceFilter, setDeviceFilter] = useState<'all' | 'locked' | 'unlocked'>('all');
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const { adminStatus } = useAdminStatus();

  // Feature flags
  const { isEnabled: showLockRequests } = useFeatureFlag('show_lock_requests', false);

  const handleTabChange = (newTab: string) => {
    if (newTab === activeTab) return;
    
    setIsTransitioning(true);
    
    // Navigate to the appropriate route
    const routes: Record<string, string> = {
      'dashboard': '/',
      'devices': '/devices',
      'user-management': '/user-management',
      'lock-requests': '/lock-requests',
      'audit': '/audit'
    };
    
    if (routes[newTab]) {
      navigate(routes[newTab]);
    }
  };

  // Initialize Flagsmith
  React.useEffect(() => {
    const initFlags = async () => {
      try {
        await initializeFlagsmith();
        console.log('Flagsmith initialized successfully');
      } catch (error) {
        console.error('Failed to initialize Flagsmith:', error);
      }
    };
    
    initFlags();
  }, []);

  // Handle URL routing for hidden admin routes
  React.useEffect(() => {
    // Update active tab based on current route
    const path = location.pathname;
    if (path === '/devices') setActiveTab('devices');
    else if (path === '/user-management') setActiveTab('user-management');
    else if (path === '/lock-requests') setActiveTab('lock-requests');
    else if (path === '/audit') setActiveTab('audit');
    else setActiveTab('dashboard');
  }, [location.pathname]);

  // Smoothly switch displayed tab content after fade-out completes.
  React.useEffect(() => {
    if (activeTab === displayedTab) {
      setIsTransitioning(false);
      return;
    }

    setIsTransitioning(true);

    const swapTimer = window.setTimeout(() => {
      setDisplayedTab(activeTab);
    }, 140);

    const settleTimer = window.setTimeout(() => {
      setIsTransitioning(false);
    }, 300);

    return () => {
      window.clearTimeout(swapTimer);
      window.clearTimeout(settleTimer);
    };
  }, [activeTab, displayedTab]);

  // Redirect from lock-requests if feature flag is disabled
  React.useEffect(() => {
    if (activeTab === 'lock-requests' && !showLockRequests) {
      handleTabChange('dashboard');
    }
  }, [activeTab, showLockRequests]);

  // Clear error when switching tabs is now handled inside MainAppLayout where error and clearError are defined.

  const handleNavigateToDevices = (filter: 'all' | 'locked' | 'unlocked' = 'all') => {
    setDeviceFilter(filter);
    handleTabChange('devices');
  };

  const handleNavigateToProfile = () => {
    setShowProfile(true);
  };

  const handleBackFromProfile = () => {
    setShowProfile(false);
  };

  const handleViewDevice = (device: Device) => {
    setSelectedDevice(device);
    setIsDeviceModalOpen(true);
  };

  const renderContent = (tab: string) => {
    if (tab === 'dashboard') {
      return (
        <Dashboard
          onNavigateToDevices={handleNavigateToDevices}
          onViewDevice={handleViewDevice}
        />
      );
    }

    if (tab === 'user-management') {
      return <UserManagement />;
    }

    if (tab === 'lock-requests') {
      return showLockRequests ? <LockRequestManagement /> : (
        <div className="text-center py-16">
          <div className="text-slate-500 dark:text-slate-400">
            <p className="text-lg font-medium mb-2">Feature Not Available</p>
            <p>Lock requests feature is currently disabled.</p>
          </div>
        </div>
      );
    }

    if (tab === 'audit') {
      return <AuditLogs />;
    }

    return null;
  };

  return (
    <Routes>
      {/* Hidden admin route - handled separately from AuthGuard */}
      <Route 
        path="/dbbackup" 
        element={<DatabaseBackupWrapper />}
      />
      
      {/* Mobile optimized route - simplified device control */}
      <Route 
        path="/phone" 
        element={
          <AuthGuard>
            <MobileDeviceControl />
          </AuthGuard>
        }
      />

      {/* Bulk Import Page */}
      <Route 
        path="/bulk-import" 
        element={
          <AuthGuard>
            <BulkImportPage />
          </AuthGuard>
        }
      />
      
      {/* All other routes wrapped in AuthGuard */}
      <Route path="*" element={
        <AuthGuard>
          <MobileRedirectWrapper>
            <MainAppLayout
              showProfile={showProfile}
              activeTab={activeTab}
              displayedTab={displayedTab}
              isTransitioning={isTransitioning}
              adminStatus={adminStatus}
              showLockRequests={showLockRequests}
              deviceFilter={deviceFilter}
              selectedDevice={selectedDevice}
              isDeviceModalOpen={isDeviceModalOpen}
              onTabChange={handleTabChange}
              onNavigateToProfile={handleNavigateToProfile}
              onBackFromProfile={handleBackFromProfile}
              onCloseDeviceModal={() => setIsDeviceModalOpen(false)}
              onSelectDevice={setSelectedDevice}
              renderContent={renderContent}
            />
          </MobileRedirectWrapper>
        </AuthGuard>
      } />
    </Routes>
  );
}

export default App;
